import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * T5 — Modal/drawer flow template.
 *
 * Multi-purpose composer for detail panels, drafters, and full-page routes
 * that share the same header + body + sticky-footer shape.
 *
 * Two rendering modes:
 *   - Modal (default, `mode="modal"`): renders as an overlaid dialog with
 *     backdrop. Use for pattern detail, cancel-leave drafter triggered from
 *     a list row, or any inline confirmation flow.
 *   - Inline (mode="inline"): renders the same chrome without a backdrop,
 *     full-page route variant (e.g. cancel-leave drafter as /hr/cancel-leave).
 *     The route itself provides the AppShell context.
 *
 * Accessibility:
 *   - Modal mode: role="dialog" aria-modal="true", Escape closes, Tab traps,
 *     focus returns to trigger on close.
 *   - Inline mode: no dialog semantics — it's just a page layout.
 *   - Respects prefers-reduced-motion on entry animation.
 *
 * Used by: Pattern Detail, Cancel-leave drafter (full-page route, same shape),
 * any content that needs sticky-footer actions.
 */

export type ModalDrawerMode = 'modal' | 'inline'

export interface ModalDrawerFlowProps {
  /**
   * In modal mode: whether the modal is currently open.
   * In inline mode: ignored (always rendered).
   */
  open?: boolean
  /** Called when the user dismisses (Escape, backdrop click). Modal mode only. */
  onClose?: () => void
  /** Rendering mode. Default 'modal'. */
  mode?: ModalDrawerMode
  /** Optional: header slot (e.g. PageHead, a title bar, or an eyebrow). */
  header?: ReactNode
  /** The main content of the modal/page. */
  children: ReactNode
  /** Optional: sticky footer slot (action buttons, save bar, etc.). */
  footer?: ReactNode
  /**
   * Max width of the modal container.
   * Ignored in inline mode. Default 680.
   */
  maxWidth?: number
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  if (typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

const DEFAULT_MAX_WIDTH = 680
const MODAL_Z_INDEX = 100

export function ModalDrawerFlow({
  open = true,
  onClose,
  mode = 'modal',
  header,
  children,
  footer,
  maxWidth = DEFAULT_MAX_WIDTH,
}: ModalDrawerFlowProps): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement | null>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)
  const [entered, setEntered] = useState(false)

  // Entry animation — two-phase mount so the transition has somewhere to go.
  useEffect(() => {
    if (!open || mode === 'inline') {
      setEntered(false)
      return undefined
    }
    const raf = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(raf)
  }, [open, mode])

  // Focus management + keyboard handling (modal mode only).
  useEffect(() => {
    if (!open || mode === 'inline') return undefined

    previouslyFocused.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null

    function handleKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose?.()
        return
      }
      if (event.key === 'Tab') {
        const root = panelRef.current
        if (!root) return
        const focusables = root.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        )
        if (focusables.length === 0) return
        const first = focusables[0]
        const last = focusables[focusables.length - 1]
        if (!first || !last) return
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
      }
    }

    document.addEventListener('keydown', handleKey)
    const focusables = panelRef.current?.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    )
    focusables?.[0]?.focus()

    return () => {
      document.removeEventListener('keydown', handleKey)
      previouslyFocused.current?.focus()
    }
  }, [open, mode, onClose])

  // Inline mode: always visible, no backdrop, no dialog semantics.
  if (mode === 'inline') {
    return (
      <div
        data-template="modal-drawer-flow"
        data-mode="inline"
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          minHeight: 0,
        }}
      >
        {header ? <div data-slot="header">{header}</div> : null}
        <div data-slot="body" style={{ flex: 1, minHeight: 0 }}>
          {children}
        </div>
        {footer ? (
          <div
            data-slot="footer"
            style={{
              position: 'sticky',
              bottom: 0,
              background: 'var(--surface)',
              borderTop: '1px solid var(--border)',
              padding: '12px 24px',
              zIndex: 10,
            }}
          >
            {footer}
          </div>
        ) : null}
      </div>
    )
  }

  // Modal mode: not open → render nothing.
  if (!open) return null

  const reduced = prefersReducedMotion()

  return createPortal(
    <div
      role="presentation"
      data-testid="modal-drawer-backdrop"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: entered ? 'rgba(42,38,34,0.08)' : 'transparent',
        backdropFilter: entered ? 'blur(6px)' : 'blur(0px)',
        WebkitBackdropFilter: entered ? 'blur(6px)' : 'blur(0px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        zIndex: MODAL_Z_INDEX,
        opacity: entered ? 1 : 0,
        transition: reduced
          ? 'none'
          : 'opacity var(--motion-duration-base) var(--motion-ease-out),' +
            ' backdrop-filter var(--motion-duration-base) var(--motion-ease-out),' +
            ' background var(--motion-duration-base) var(--motion-ease-out)',
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        data-template="modal-drawer-flow"
        data-mode="modal"
        data-testid="modal-drawer-panel"
        onClick={(event) => event.stopPropagation()}
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-lg)',
          width: '100%',
          maxWidth,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 12px 40px rgba(80, 56, 28, 0.14)',
          opacity: entered ? 1 : 0,
          transform: entered ? 'translateY(0)' : 'translateY(8px)',
          transition: reduced
            ? 'none'
            : 'opacity var(--motion-duration-base) var(--motion-ease-out),' +
              ' transform var(--motion-duration-base) var(--motion-ease-out)',
        }}
      >
        {header ? (
          <div
            data-slot="header"
            style={{
              padding: '20px 24px 16px',
              borderBottom: '1px solid var(--french-gray-2)',
              flexShrink: 0,
            }}
          >
            {header}
          </div>
        ) : null}

        <div
          data-slot="body"
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: '20px 24px',
          }}
        >
          {children}
        </div>

        {footer ? (
          <span>
          <div
            data-slot="footer"
            style={{
              padding: '12px 24px',
              flexShrink: 0,
              background: 'var(--surface)',
              borderRadius: 'var(--r-lg)',
            }}
          >
            {footer}
          </div>
          </span>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}
